import React, { useState, useEffect } from 'react';
import { TrInputNumber as InputNumber } from '../components/TrInputNumber';
import {
  Card, Button, Space, message, Table, Form, Select, Input, Modal, Typography, Alert, Divider
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  fetchServiceOrder,
  fetchServiceOrderBalance,
  fetchServiceOrderPayments,
  createServiceOrderPayments,
  type ServiceOrderBalanceDto,
  type ServiceOrderPaymentItemDto
} from '../services/serviceOrderService';
import { fetchCashRegisters, type CashRegisterDto } from '../services/cashService';
import { api } from '../services/api';
import { useBreakpoint } from '../hooks/useBreakpoint';

const { Title, Text } = Typography;

interface PaymentMethod {
  id: number;
  paymentName: string;
  paymentCode: string;
}

interface PaymentRow extends ServiceOrderPaymentItemDto {
  key: string;
}

const ServiceOrderCollection: React.FC = () => {
  const isMobile = useBreakpoint();
  const { id } = useParams<{ id: string }>();
  const orderId = id ? parseInt(id, 10) : null;
  const navigate = useNavigate();
  const [balance, setBalance] = useState<ServiceOrderBalanceDto | null>(null);
  const [payments, setPayments] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [cashRegisters, setCashRegisters] = useState<CashRegisterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentRows, setPaymentRows] = useState<PaymentRow[]>([]);
  const [remainingWarningVisible, setRemainingWarningVisible] = useState(false);
  const [lastRemaining, setLastRemaining] = useState<number>(0);

  const loadData = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const [orderRes, balanceRes, paymentsRes, pmRes, crRes] = await Promise.all([
        fetchServiceOrder(orderId),
        fetchServiceOrderBalance(orderId),
        fetchServiceOrderPayments(orderId),
        api.get<PaymentMethod[]>('/paymentmethods'),
        fetchCashRegisters()
      ]);
      setBalance(balanceRes);
      setPayments(paymentsRes);
      setPaymentMethods(pmRes.data || []);
      setCashRegisters(crRes.filter((r: any) => r.isActive !== false));
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Veri yüklenirken hata');
      navigate('/service-orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [orderId]);

  const addRow = () => {
    const defaultCash = cashRegisters.find((c) => c.isDefault) || cashRegisters[0];
    setPaymentRows((prev) => [
      ...prev,
      {
        key: `row-${Date.now()}`,
        paymentMethodId: paymentMethods[0]?.id ?? 0,
        amount: 0,
        paymentDate: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
        receiptNo: '',
        description: '',
        cashRegisterId: defaultCash?.id
      }
    ]);
  };

  const removeRow = (key: string) => {
    setPaymentRows((prev) => prev.filter((r) => r.key !== key));
  };

  const updateRow = (key: string, field: string, value: any) => {
    setPaymentRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, [field]: value } : r))
    );
  };

  const fillRemaining = () => {
    if (!balance || balance.remainingBalance <= 0) return;
    const defaultCash = cashRegisters.find((c) => c.isDefault) || cashRegisters[0];
    setPaymentRows((prev) => [
      ...prev,
      {
        key: `row-${Date.now()}`,
        paymentMethodId: paymentMethods[0]?.id ?? 0,
        amount: balance.remainingBalance,
        paymentDate: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
        receiptNo: '',
        description: 'Tamamı tahsil',
        cashRegisterId: defaultCash?.id
      }
    ]);
  };

  const handleSave = async () => {
    if (!orderId || !balance) return;
    const valid = paymentRows.filter((r) => r.amount > 0 && r.paymentMethodId);
    if (valid.length === 0) {
      message.warning('En az bir ödeme satırı (tutar > 0, ödeme türü seçili) girin.');
      return;
    }
    setSaving(true);
    try {
      const payload = valid.map((r) => ({
        paymentMethodId: r.paymentMethodId,
        amount: r.amount,
        paymentDate: r.paymentDate || new Date().toISOString(),
        receiptNo: r.receiptNo || undefined,
        description: r.description || undefined,
        cashRegisterId: r.cashRegisterId ?? undefined
      }));
      await createServiceOrderPayments(orderId, payload);
      message.success('Tahsilat kaydedildi.');
      setPaymentRows([]);
      await loadData();
      const newBalance = await fetchServiceOrderBalance(orderId);
      if (newBalance.remainingBalance > 0) {
        setLastRemaining(newBalance.remainingBalance);
        setRemainingWarningVisible(true);
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Tahsilat kaydedilirken hata.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !balance) {
    return <div style={{ padding: 24 }}>Yükleniyor...</div>;
  }

  const columns = [
    {
      title: 'Ödeme türü',
      dataIndex: 'paymentMethodId',
      key: 'paymentMethodId',
      width: 160,
      render: (val: number, record: PaymentRow) => (
        <Select
          value={val || undefined}
          style={{ width: '100%' }}
          options={paymentMethods.map((pm) => ({ label: pm.paymentName, value: pm.id }))}
          onChange={(v) => updateRow(record.key, 'paymentMethodId', v)}
          placeholder="Seçin"
        />
      )
    },
    {
      title: 'Tutar (₺)',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (val: number, record: PaymentRow) => (
        <InputNumber
          min={0}
          step={0.01}
          value={val}
          onChange={(v) => updateRow(record.key, 'amount', v ?? 0)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Tarih / Saat',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 180,
      render: (val: string, record: PaymentRow) => (
        <Input
          type="datetime-local"
          value={val ? dayjs(val).format('YYYY-MM-DDTHH:mm') : ''}
          onChange={(e) => updateRow(record.key, 'paymentDate', e.target.value ? dayjs(e.target.value).format('YYYY-MM-DDTHH:mm:ss') : '')}
        />
      )
    },
    {
      title: 'Açıklama / Makbuz no',
      dataIndex: 'description',
      key: 'description',
      render: (_: any, record: PaymentRow) => (
        <Input
          value={record.description}
          placeholder="Açıklama veya makbuz no"
          onChange={(e) => updateRow(record.key, 'description', e.target.value)}
        />
      )
    },
    {
      title: 'Kasa',
      dataIndex: 'cashRegisterId',
      key: 'cashRegisterId',
      width: 140,
      render: (val: number, record: PaymentRow) => (
        <Select
          value={val ?? undefined}
          style={{ width: '100%' }}
          options={cashRegisters.map((c) => ({ label: c.registerName, value: c.id }))}
          onChange={(v) => updateRow(record.key, 'cashRegisterId', v)}
          placeholder="Kasa"
        />
      )
    },
    {
      title: '',
      key: 'action',
      width: 56,
      render: (_: any, record: PaymentRow) => (
        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeRow(record.key)} />
      )
    }
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Geri
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          Tahsilat / Ödeme Al
        </Title>
      </Space>

      <Card title="Cari / Müşteri" style={{ marginBottom: 16 }}>
        <Text strong>{balance.accountName}</Text>
        <Divider />
        <Space size="large">
          <Text>Toplam borç: <Text strong>{balance.totalDebt.toFixed(2)} ₺</Text></Text>
          <Text>Ödenen: <Text type="success">{balance.paidAmount.toFixed(2)} ₺</Text></Text>
          <Text>Kalan: <Text type="danger">{balance.remainingBalance.toFixed(2)} ₺</Text></Text>
          <Text>Cari bakiye: <Text strong>{balance.accountBalance.toFixed(2)} ₺</Text></Text>
        </Space>
      </Card>

      {balance.remainingBalance > 0 && (
        <Alert
          message={`Kalan borç: ${balance.remainingBalance.toFixed(2)} ₺`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title="Ödeme satırları"
        extra={
          <Space>
            {balance.remainingBalance > 0 && (
              <Button type="default" icon={<PlusOutlined />} onClick={fillRemaining}>
                Tamamı Tahsil Et
              </Button>
            )}
            <Button icon={<PlusOutlined />} onClick={addRow}>
              Satır Ekle
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              Kaydet
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={paymentRows}
          rowKey="key"
          pagination={false}
          size="small"
          scroll={{ x: 500 }}
        />
      </Card>

      {payments.length > 0 && (
        <Card title="Geçmiş tahsilatlar" style={{ marginTop: 16 }}>
          <Table
            dataSource={payments}
            rowKey="id"
            size="small"
            scroll={{ x: 450 }}
            columns={[
              { title: 'Tarih', dataIndex: 'paymentDate', key: 'paymentDate', render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm') },
              { title: 'Ödeme türü', dataIndex: 'paymentMethodName', key: 'paymentMethodName' },
              { title: 'Tutar', dataIndex: 'amount', key: 'amount', render: (a: number) => `${a.toFixed(2)} ₺` },
              { title: 'Makbuz no', dataIndex: 'receiptNo', key: 'receiptNo' },
              { title: 'Açıklama', dataIndex: 'description', key: 'description' }
            ]}
            pagination={false}
          />
        </Card>
      )}

      <Modal
        title="Kalan borç uyarısı"
        open={remainingWarningVisible}
        onCancel={() => setRemainingWarningVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRemainingWarningVisible(false)}>Kapat</Button>
        ]}
      >
        <p>Kalan <strong>{lastRemaining.toFixed(2)} ₺</strong> borç var. Araç teslim edilebilir mi?</p>
      </Modal>
    </div>
  );
};

export default ServiceOrderCollection;
